// React imports
import React, { useEffect, useState } from "react";

// MUI imports
import { Autocomplete, FormControl, MenuItem, Select, TextField } from "@mui/material";

// Customized MUI components
import MDBox from "components/MDBox";
import MDTypography from "components/MDTypography";

// Other imports
import PropTypes from "prop-types";
import { useAuth } from "auth/Auth";
import { useCountryCodes } from "hooks/useCountryCodes";

const MODE_OPTIONS = [
    { value: "new", label: "Create new account" },
    { value: "existing", label: "Use existing account" },
];

// "John Michael Smith" -> first: John, middle: Michael, last: Smith
// Two words -> first + last. One word -> first name only.
const splitName = (fullName) => {
    const parts = (fullName || "").trim().split(/\s+/).filter(Boolean);
    if (parts.length === 0) return { first_name: "", middle_name: "", last_name: "" };
    if (parts.length === 1) return { first_name: parts[0], middle_name: "", last_name: "" };
    return {
        first_name: parts[0],
        middle_name: parts.slice(1, -1).join(" "),
        last_name: parts[parts.length - 1],
    };
};

// Fills whatever the account can provide. Fields the account has no value for are left untouched.
const buildPrefill = (account, countryCodes) => {
    const updates = { ...splitName(account.name) };
    if (account.email) updates.email = account.email;
    if (account.billing_address) updates.address = account.billing_address;
    if (account.billing_country) updates.country = account.billing_country;

    let dialCode = account.dial_code || "";
    let phone = account.phone || account.mobile || "";

    // Older accounts have no dial_code: if the number is stored as "+91 98765 43210", split it.
    if (!dialCode && phone.trim().startsWith("+")) {
        const compact = phone.replace(/[^\d+]/g, "");
        const match = [...countryCodes]
            .sort((a, b) => b.dialCode.length - a.dialCode.length)
            .find((c) => compact.startsWith(c.dialCode));
        if (match) {
            dialCode = match.dialCode;
            phone = compact.slice(match.dialCode.length);
        }
    }
    if (dialCode) updates.phoneCountryCode = dialCode;
    if (phone) updates.phone = dialCode ? phone.replace(/\D/g, "") : phone;

    return updates;
};

const optionLabel = (account) =>
    account?.email ? `${account.name} (${account.email})` : account?.name || "";

function PrimaryAccountSelector({ traveler, onChange, modeError, accountError, onClearErrors, disabled = false }) {
    const { axiosInstance } = useAuth();
    const { countryCodes } = useCountryCodes();

    const [options, setOptions] = useState([]);
    const [inputValue, setInputValue] = useState("");
    const [loading, setLoading] = useState(false);
    const [selected, setSelected] = useState(null);

    const isExisting = traveler.accountMode === "existing";

    // Search accounts (the endpoint is already permission-scoped for the current user).
    useEffect(() => {
        if (!isExisting) return undefined;
        let cancelled = false;
        const timer = setTimeout(async () => {
            setLoading(true);
            try {
                const res = await axiosInstance.get("/accounts/", {
                    params: { search: inputValue || undefined },
                });
                if (!cancelled) {
                    const accounts = Array.isArray(res.data) ? res.data : [];
                    setOptions(accounts.filter((a) => (a.account_type || "") !== "Partner"));
                }
            } catch (err) {
                if (!cancelled) setOptions([]);
            } finally {
                if (!cancelled) setLoading(false);
            }
        }, 300);
        return () => {
            cancelled = true;
            clearTimeout(timer);
        };
    }, [inputValue, isExisting]);

    // An account is already linked (edit mode, or just saved): load it so the picker can show its name.
    useEffect(() => {
        if (!isExisting || !traveler.accountId || selected?.id === traveler.accountId) return undefined;
        let cancelled = false;
        (async () => {
            try {
                const res = await axiosInstance.get(`/accounts/${traveler.accountId}`);
                if (!cancelled) setSelected(res.data);
            } catch (err) {
                if (!cancelled) setSelected({ id: traveler.accountId, name: `Account #${traveler.accountId}` });
            }
        })();
        return () => {
            cancelled = true;
        };
    }, [isExisting, traveler.accountId]);

    const handleModeChange = (mode) => {
        onClearErrors?.();
        if (mode === "new") {
            setSelected(null);
            onChange({ accountMode: "new", accountId: null });
        } else {
            onChange({ accountMode: "existing" });
        }
    };

    const handleAccountChange = (account) => {
        onClearErrors?.();
        setSelected(account);
        if (!account) {
            onChange({ accountId: null });
            return;
        }
        onChange({ accountId: account.id, ...buildPrefill(account, countryCodes) });
    };

    return (
        <MDBox mb={2.5} p={2} borderRadius="lg" sx={{ bgcolor: "#F7FAFF", border: "1px solid #E3F2FD" }}>
            <MDTypography variant="button" fontWeight="bold" display="block" mb={1.5}>
                Customer Account
            </MDTypography>

            <MDBox display="flex" flexWrap="wrap" gap={2}>
                <MDBox flex="1 1 220px" minWidth={200} id={`field-${traveler.id}-accountMode`}>
                    <MDTypography variant="caption" fontWeight="bold" color="text" display="block" mb={0.5}>
                        Account{" "}
                        <MDBox component="span" sx={{ color: "#E53935" }}>*</MDBox>
                    </MDTypography>
                    <FormControl fullWidth size="small" error={Boolean(modeError)}>
                        <Select
                            displayEmpty
                            disabled={disabled}
                            value={traveler.accountMode || ""}
                            onChange={(e) => handleModeChange(e.target.value)}
                        >
                            <MenuItem value="" disabled>
                                Select an option
                            </MenuItem>
                            {MODE_OPTIONS.map((opt) => (
                                <MenuItem key={opt.value} value={opt.value}>
                                    {opt.label}
                                </MenuItem>
                            ))}
                        </Select>
                    </FormControl>
                    {modeError && (
                        <MDTypography variant="caption" color="error" display="block" mt={0.5}>
                            {typeof modeError === "string" ? modeError : "Select an account option."}
                        </MDTypography>
                    )}
                </MDBox>

                {isExisting && (
                    <MDBox flex="1 1 320px" minWidth={240} id={`field-${traveler.id}-accountId`}>
                        <MDTypography variant="caption" fontWeight="bold" color="text" display="block" mb={0.5}>
                            Search account{" "}
                            <MDBox component="span" sx={{ color: "#E53935" }}>*</MDBox>
                        </MDTypography>
                        <Autocomplete
                            size="small"
                            disabled={disabled}
                            options={options}
                            loading={loading}
                            value={selected}
                            filterOptions={(x) => x} /* filtering is done by the API */
                            getOptionLabel={optionLabel}
                            isOptionEqualToValue={(a, b) => a.id === b.id}
                            onChange={(_, value) => handleAccountChange(value)}
                            onInputChange={(_, value, reason) => {
                                if (reason === "input") setInputValue(value);
                            }}
                            renderInput={(params) => (
                                <TextField
                                    {...params}
                                    placeholder="Search by account name"
                                    error={Boolean(accountError)}
                                />
                            )}
                        />
                        {accountError && (
                            <MDTypography variant="caption" color="error" display="block" mt={0.5}>
                                {typeof accountError === "string" ? accountError : "Select an account."}
                            </MDTypography>
                        )}
                    </MDBox>
                )}
            </MDBox>

            <MDTypography variant="caption" color="text" display="block" mt={1}>
                {isExisting
                    ? "Details below are filled from the account. Email, phone and address changes you make will update the account when the booking is saved."
                    : traveler.accountMode === "new"
                        ? "A new account will be created for the primary traveler when the booking is saved."
                        : "Choose whether this booking belongs to an existing account or a new one."}
            </MDTypography>
        </MDBox>
    );
}

PrimaryAccountSelector.propTypes = {
    disabled: PropTypes.bool,
    traveler: PropTypes.object.isRequired,
    onChange: PropTypes.func.isRequired,
    modeError: PropTypes.oneOfType([PropTypes.bool, PropTypes.string]),
    accountError: PropTypes.oneOfType([PropTypes.bool, PropTypes.string]),
    onClearErrors: PropTypes.func,
};

export default PrimaryAccountSelector;